import { CreateAccountResponse } from './../classes/createAccountResponse';
import { Integrations } from './../enums/integrations';
import { AccountData } from './../classes/accountData';
import { AppResponse } from 'modules/core/interfaces/appResponse';
import { CreatorAccountInfo } from '../classes/creatorAccountInfo';
import axios, { AxiosResponse, AxiosPromise, AxiosError } from 'axios';
import { ContactProperty } from './../classes/contactProperty';
import environment from '@/environment';
import { Creator } from '../classes/creator';
import { ConfigureIntegrationData } from '../classes/configureIntegrationData';
import { IntegrationApiKeys } from '../classes/integrationApiKeys';
import AuthorizedApiClient from 'modules/auth/models/authorizedApiClient';
import store from '@/store';

export class InstallationWizardApi {
  public async createAccount(integration: Integrations, account: AccountData):
  Promise<CreateAccountResponse | undefined> {
    return axios.post(environment.backendUrl + '/api/v1.0/creator/create', {
      integration,
      ...account,
    }).then((response: AxiosResponse<AppResponse<CreateAccountResponse>>) => {
      if (response.status === 201) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return responseData.Result;
        } else {
          throw new Error(responseData.ErrorCodes.join(','));
        }
      }
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<CreateAccountResponse> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async checkIfIntegrationExists(email: string, integrationType: Integrations): Promise<boolean | undefined> {
    return axios.get(environment.backendUrl + '/api/v1.0/account/checkIfUserHasIntegration?email=' + email + '&integrationType=' + integrationType).then((response: AxiosResponse<AppResponse<boolean>>) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return responseData.Result;
        } else {
          throw new Error(responseData.ErrorCodes.join(','));
        }
      }
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<boolean> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async checkIfIntegrationExistsByDomainAndType(domain: string, integrationType: Integrations):
  Promise<boolean | undefined> {
    const params = '?domain=' + domain + '&integrationType=' + integrationType;
    return axios.get(
      environment.backendUrl + '/api/v1.0/account/CheckIfIntegrationExistsByDomainAndType' + params,
    ).then((response: AxiosResponse<AppResponse<boolean>>) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return responseData.Result;
        } else {
          throw new Error(responseData.ErrorCodes.join(','));
        }
      }
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<boolean> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async integrationCallback(callback: string, data: any): Promise<any> {
    const response: AxiosResponse<any> = await axios.post(callback, data);
    if (response.status === 200) {
      return response.data;
    } else {
      throw new Error('Callback error');
    }
  }

  public async getProps(integration: Integrations): Promise<ContactProperty[] | undefined> {
    return AuthorizedApiClient.get(environment.backendUrl + '/creator/GetProperties?integration=' + integration,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response: AxiosResponse<AppResponse<ContactProperty[]>>) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return responseData.Result;
        } else {
          throw new Error(responseData.ErrorCodes.join(','));
        }
      }
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<ContactProperty[]> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async getCreatorAccountInfo(email: string): Promise<CreatorAccountInfo | undefined> {
    const url = environment.backendUrl + '/api/v1.0/creator/GetCreatorAccountInfo?email=' + encodeURIComponent(email);
    return axios.get(url).then((response: AxiosResponse<AppResponse<CreatorAccountInfo>>) => {
      if (response.status === 200) {
        const responseData = response.data;

        if (responseData.IsSuccess) {
          return responseData.Result;
        } else {
          throw new Error(responseData.ErrorCodes.join(','));
        }
      }
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<CreatorAccountInfo> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async createCreator(creator: Creator): Promise<boolean> {
    return axios.post(environment.backendUrl + '/api/v1.0/creator/start', creator,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response: AxiosResponse<AppResponse<null>>) => {
      return true;
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<null> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async configureIntegration(data: ConfigureIntegrationData): Promise<any> {
    return AuthorizedApiClient.post(environment.backendUrl + '/creator/ConfigureIntegration', data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response: AxiosResponse<AppResponse<any>>) => {
      return response.data;
    }).catch((error: AxiosError) => {
      if (error.response && error.response.data) {
        const responseData: AppResponse<null> = error.response.data;
        throw new Error(responseData.ErrorCodes.join(','));
      } else {
        throw new Error();
      }
    });
  }

  public async checkIfUserIsLogin(): Promise<boolean> {
    let user = null;

    if (store.state.auth && store.state.auth.user) {
      user = store.state.auth.user;
    }

    if (user && user.access_token) {
      return axios.get(environment.backendUrl  + environment.apiPrefix + '/account/IsUserLogin',
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + user.access_token,
        },
      }).then((response: AxiosResponse<any>) => {
        return true;
      }).catch((error: AxiosError) => {
        return false;
      });
    }

    return Promise.resolve(false);
  }

  public async ValidateApiKey(apiKeys: IntegrationApiKeys, integration: string): Promise<boolean> {
    const data = {
      apiKey: apiKeys.apiKey,
      clientId: apiKeys.clientId,
      clientSecret: apiKeys.clientSecret,
      tenantName: apiKeys.url,
      integration: integration,
    };
    return AuthorizedApiClient.post(environment.backendUrl + '/creator/ValidateApiKey', data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    }).then((response: AxiosResponse<AppResponse<boolean>>) => {
      return response.data.Result;
    })
  }
}
